import { useCallback, useEffect, useMemo, useRef } from "react";
import { Navigate, Outlet, useLocation, useMatch } from "react-router";
import type { SDBasketType } from "../../db";
import {
  DISCOVER_DISTRIBUTION,
  UNIFIED_CHECKOUT_BASKET,
  UNIFIED_CHECKOUT_BASKET_OVERVIEW,
  UNIFIED_CHECKOUT_BASKET_SUCCESS,
} from "../../constants/routes";
import { useDraftOrderList } from "../../hooks/api/v2/draft-order/useDraftOrderList";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import { useCurrentBusinessUser } from "../../hooks/api/v2/business/useCurrentBusinessUser";
import { useSellarDistributionContactList } from "../../hooks/api/v2/sellarDistributionContact/useSellarDistributionContactList";
import { UnifiedCheckoutProvider } from "./UnifiedCheckoutProvider";
import { SupplierStorefrontInfo } from "./UnifiedCheckoutProvider.shared";
import {
  useUnifiedCheckoutBasket,
  useUnifiedCheckoutBasketUpdate,
} from "../../hooks/api/v2/unified-checkout-basket/useUnifiedCheckoutBaskets";
import { useUnifiedCheckoutDraftOrders } from "./hooks/useUnifiedCheckoutDraftOrders";
import dayjs from "dayjs";
import { toISODateString } from "../../types/isoDate";
import { useQueries } from "@tanstack/react-query";
import {
  apiV2AssertResponse,
  apiV2Request,
} from "../../hooks/api/v2/apiV2Request";
import type { SupplierStorefrontV2GetResponseType } from "@sellar-io/api-v2/types";
import { ServerStateKeys } from "../../hooks/api/v2/ServerStateKeys";
import { useFirebaseUser } from "../../hooks/useFirebaseUser.shared";
import { getUnifiedCheckoutGuardRedirect } from "./getUnifiedCheckoutGuardRedirect";
import { RyftCardPaymentProvider } from "../stockList/checkoutV2/lib/RyftCardPaymentProvider";

/**
 * Layout route for the guarded unified checkout steps. Loads the SDBasket and
 * its draft orders from Dexie, resolves contacts and default basket values,
 * handles routing guards, then renders the unified checkout context provider
 * around the matched child route. Keeps the provider itself reusable and
 * unopinionated about data fetching or routing.
 */
export const UnifiedCheckoutGuard = () => {
  const sdBasketUuid = useMatch({
    path: UNIFIED_CHECKOUT_BASKET.pattern,
    end: false,
  })?.params.sdBasketUuid;
  const location = useLocation();
  const { business } = useSellarCurrentBusiness();
  const { user } = useFirebaseUser();
  const { data: businessUser } = useCurrentBusinessUser(business?.id);
  const contactsQuery = useSellarDistributionContactList({ limit: 100 });

  // Load the SDBasket by UUID from Dexie. Resolve to `null` when the basket
  // does not exist so we can distinguish "not found" from "still loading".
  const basketQuery = useUnifiedCheckoutBasket(sdBasketUuid);
  const { mutateAsync: persistBasket } = useUnifiedCheckoutBasketUpdate(
    sdBasketUuid ?? "",
  );
  const backendBasket = basketQuery.data;
  const sdBasket = useMemo<SDBasketType | null | undefined>(
    () =>
      !user || !business || basketQuery.isPending
        ? undefined
        : backendBasket
          ? {
              uuid: backendBasket.uuid,
              retailerId: business?.Retailer?.id ?? 0,
              createdAt: backendBasket.createdAt.toString(),
              deliveryDate: backendBasket.requestedDeliveryAt
                ? toISODateString(
                    dayjs(backendBasket.requestedDeliveryAt).format(
                      "YYYY-MM-DD",
                    ),
                  )
                : null,
              orderCutoffDate: backendBasket.orderCutoffAt?.toString() ?? null,
              deliveryAddress:
                backendBasket.DeliveryAddress as SDBasketType["deliveryAddress"],
              phoneNumber: backendBasket.phoneNumber,
              deliveryInstructions: backendBasket.deliveryInstructions,
              ordersComment: backendBasket.ordersComment,
              hasNoDraughtDutyRelief: backendBasket.hasNoDraughtDutyRelief,
              deliveryPreference: backendBasket.deliveryPreference,
              contactId: backendBasket.SellarDistributionContactId,
              timeSlotPreference:
                backendBasket.timePreference === "allDay"
                  ? "flexible"
                  : (backendBasket.timePreference?.toUpperCase() as
                      | "AM"
                      | "PM"
                      | undefined),
              paidOrderIds: backendBasket.Orders.filter(
                (order) =>
                  order.paymentType === "manual" ||
                  order.paymentStatus === "paid",
              ).map((order) => order.id),
              paidSupplierIds: backendBasket.Orders.filter(
                (order) =>
                  order.paymentType === "manual" ||
                  order.paymentStatus === "paid",
              ).map((order) => order.SupplierId),
              failedOrderIds: backendBasket.Orders.filter(
                (order) => order.paymentStatus === "failed",
              ).map((order) => order.id),
              failedSupplierIds: backendBasket.Orders.filter(
                (order) => order.paymentStatus === "failed",
              ).map((order) => order.SupplierId),
              pendingOrderIds: backendBasket.Orders.filter(
                (order) =>
                  order.paymentType !== "manual" &&
                  order.paymentStatus !== "paid" &&
                  order.paymentStatus !== "failed",
              ).map((order) => order.id),
              pendingSupplierIds: backendBasket.Orders.filter(
                (order) =>
                  order.paymentType !== "manual" &&
                  order.paymentStatus !== "paid" &&
                  order.paymentStatus !== "failed",
              ).map((order) => order.SupplierId),
            }
          : null,
    [backendBasket, basketQuery.isPending, business, user],
  );

  const hasAppliedDefaultsRef = useRef(false);
  const hasSelectedDefaultContactRef = useRef(false);

  // Load all draft orders belonging to this basket
  const { orders: sdOrders, isLoading: ordersLoading } =
    useUnifiedCheckoutDraftOrders(backendBasket ? [backendBasket] : []);

  // Get supplier IDs from orders (must be called before early return)
  const supplierIds = useMemo(
    () =>
      sdOrders
        ?.map((order) => order.SupplierId)
        .filter((id): id is number => id != null) ?? [],
    [sdOrders],
  );

  // Fetch supplier info (must be called before early return)
  const draftOrderListQuery = useDraftOrderList(
    {
      query: [
        {
          type: "supplierIdList",
          modifier: "IN",
          value: supplierIds,
        },
      ],
      limit: 20,
    },
    { enabled: supplierIds.length > 0 },
  );
  const sdStorefrontQueries = useQueries({
    queries: supplierIds.map((supplierId) => ({
      queryKey: [
        business?.id,
        ServerStateKeys.SupplierStorefrontV2,
        supplierId,
        { fulfillmentType: "sellarDistribution" },
      ],
      queryFn: () =>
        apiV2Request({
          user,
          business,
          method: "get",
          path: `suppliers/${supplierId}/storefront/v2`,
          params: { fulfillmentType: "sellarDistribution" },
        }).then(
          (response) =>
            apiV2AssertResponse<SupplierStorefrontV2GetResponseType>(response)
              .data,
        ),
      enabled: Boolean(user && business),
    })),
  });

  // Create supplier info map (must be called before early return)
  const supplierInfoMap = useMemo(() => {
    const map = new Map<number, SupplierStorefrontInfo>();
    for (const page of draftOrderListQuery.data?.pages ?? []) {
      for (const info of page) {
        const supplierId = info.supplier.id;
        const storefront =
          sdStorefrontQueries[supplierIds.indexOf(supplierId)]?.data;
        map.set(supplierId, {
          ...info,
          storefront: storefront ?? info.storefront,
        });
      }
    }
    return map;
  }, [draftOrderListQuery.data, sdStorefrontQueries, supplierIds]);

  const updateBasket = useCallback(
    async (updates: Partial<SDBasketType>) => {
      if (!sdBasketUuid) return;
      await persistBasket({
        deliveryPreference: updates.deliveryPreference ?? undefined,
        timePreference:
          updates.timeSlotPreference === "flexible"
            ? "allDay"
            : (updates.timeSlotPreference?.toLowerCase() as
                | "am"
                | "pm"
                | undefined),
        DeliveryAddress: updates.deliveryAddress ?? undefined,
        phoneNumber: updates.phoneNumber ?? undefined,
        deliveryInstructions: updates.deliveryInstructions ?? undefined,
        ordersComment: updates.ordersComment ?? undefined,
        hasNoDraughtDutyRelief: updates.hasNoDraughtDutyRelief ?? undefined,
        SellarDistributionContactId: updates.contactId ?? undefined,
      });
    },
    [persistBasket, sdBasketUuid],
  );

  // Auto-select an existing Sellar Distribution contact for the current user on
  // load. New contacts are created manually via SellarDistributionContactCard on
  // the details page.
  useEffect(() => {
    if (!sdBasket) return;
    if (contactsQuery.isLoading) return;
    if (sdBasket.contactId) return;
    if (hasSelectedDefaultContactRef.current) return;

    const contacts = contactsQuery.data?.pages.flat() ?? [];
    const currentUserContact = contacts.find(
      (contact) => contact.BusinessUserId === businessUser?.id,
    );

    if (currentUserContact) {
      hasSelectedDefaultContactRef.current = true;
      updateBasket({ contactId: currentUserContact.id }).catch(() => undefined);
    }
  }, [businessUser, contactsQuery, sdBasket, updateBasket]);

  // Apply the remaining defaults once on checkout load. Existing values are
  // preserved so the details page can override them later without losing user edits.
  useEffect(() => {
    if (!sdBasket) return;
    if (hasAppliedDefaultsRef.current) return;
    if (contactsQuery.isLoading) return;

    const defaults: Partial<SDBasketType> = {};

    if (!sdBasket.deliveryAddress && business?.DeliveryAddress) {
      defaults.deliveryAddress = {
        name: business.DeliveryAddress.name || "",
        street: business.DeliveryAddress.street || "",
        city: business.DeliveryAddress.city || "",
        region: business.DeliveryAddress.region || "",
        postalCode: business.DeliveryAddress.postalCode || "",
        country: business.DeliveryAddress.country,
      };
    }
    if (!sdBasket.phoneNumber && business?.phoneNumber) {
      defaults.phoneNumber = business.phoneNumber;
    }
    if (!sdBasket.deliveryInstructions && business?.deliveryInstructions) {
      defaults.deliveryInstructions = business.deliveryInstructions;
    }

    Object.assign(defaults, {
      deliveryInstructions:
        defaults.deliveryInstructions ?? sdBasket.deliveryInstructions ?? "",
      ordersComment: sdBasket.ordersComment ?? "",
      deliveryPreference: sdBasket.deliveryPreference ?? "consolidated",
      timeSlotPreference: sdBasket.timeSlotPreference ?? "flexible",
    });

    if (Object.keys(defaults).length > 0) {
      updateBasket(defaults);
    }
    hasAppliedDefaultsRef.current = true;
  }, [sdBasket, business, contactsQuery, updateBasket]);

  // Still loading from Dexie – render nothing until resolved
  if (sdBasket === undefined || ordersLoading) {
    return null;
  }

  // Basket not found – redirect back to discovery
  if (sdBasket === null) {
    return <Navigate to={DISCOVER_DISTRIBUTION} replace />;
  }

  const isSuccessPage = location.pathname.endsWith("/success");
  const isOverviewPage = location.pathname.endsWith("/overview");
  const redirect = getUnifiedCheckoutGuardRedirect({
    draftOrderCount: sdOrders.length,
    paidOrderCount: sdBasket.paidOrderIds?.length ?? 0,
    pendingOrderCount: sdBasket.pendingOrderIds?.length ?? 0,
    failedOrderCount: sdBasket.failedOrderIds?.length ?? 0,
    isSuccessPage,
    isOverviewPage,
  });

  // An empty draft-order list only means checkout is complete when the basket
  // contains placed orders. Removing the final draft item should instead leave
  // the buyer on the overview empty state.
  if (redirect === "success") {
    return (
      <Navigate
        to={UNIFIED_CHECKOUT_BASKET_SUCCESS.href({
          sdBasketUuid: sdBasket.uuid,
        })}
        replace
      />
    );
  }

  if (redirect === "overview") {
    return (
      <Navigate
        to={UNIFIED_CHECKOUT_BASKET_OVERVIEW.href({
          sdBasketUuid: sdBasket.uuid,
        })}
        replace
      />
    );
  }

  // Draft orders still exist but the user landed on the success page. Send them
  // back to the checkout flow so they can finish paying.
  if (redirect === "checkout") {
    return (
      <Navigate
        to={UNIFIED_CHECKOUT_BASKET.href({ sdBasketUuid: sdBasket.uuid })}
        replace
      />
    );
  }

  return (
    <UnifiedCheckoutProvider
      sdBasket={sdBasket}
      sdOrders={sdOrders}
      supplierInfoMap={supplierInfoMap}
      isLoading={
        draftOrderListQuery.isLoading ||
        sdStorefrontQueries.some((query) => query.isLoading)
      }
      updateBasket={updateBasket}
    >
      <RyftCardPaymentProvider>
        <Outlet />
      </RyftCardPaymentProvider>
    </UnifiedCheckoutProvider>
  );
};
